import { useEffect, useState } from 'react';
import { Banner, Button, Card, Dialog, Field, formatMoney, Money, PageHeader, Pill } from '@elysian/ui';
import type { FxFeedCredentials, FxOverview, FxRate, Revaluation } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, shortDate } from '../invoices/shared';
import { currencyGroups } from '../shared/currencies';

const SOURCE: Record<FxRate['source'], string> = {
  base: 'Base currency', manual: 'Typed in', ecb: 'ECB', hmrc: 'HMRC', wise: 'Wise', revolut: 'Revolut',
};
const KIND: Record<Revaluation['lines'][number]['kind'], string> = { debtors: 'Customer', creditors: 'Supplier', bank: 'Bank account' };
const when = (s: string) => new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin' }).format(new Date(s));
const rate = (n: number) => n.toFixed(6).replace(/0{1,2}$/, '').replace(/(\.\d{4})0$/, '$1');
const signed = (n: number, ccy: string) => formatMoney(n.toFixed(2), ccy);

/** The last day of the month before the given date. */
function lastMonthEnd(today: string): string {
  const d = new Date(`${today.slice(0, 7)}-01T12:00:00Z`);
  d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
}

export function Currencies() {
  const { source, today } = useSession();
  const toast = useToast((s) => s.show);
  const now = iso(today());
  const [version, setVersion] = useState(0);
  const data = useAsync(() => source.fxOverview(now), [source, now, version], { keep: true });
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<'wise' | 'revolut' | null>(null);
  const reload = () => setVersion((v) => v + 1);

  async function run(fn: () => Promise<unknown>, done: string) {
    setError(null);
    try {
      await fn();
      toast(done);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const fx = data.state === 'ready' ? data.data : null;
  return (
    <>
      <PageHeader title="Currencies"
        subtitle={`Books kept in ${fx?.base_currency ?? 'EUR'}. Invoices and bills in other currencies are posted at the day’s rate; payments at what the bank actually paid.`} />
      {data.state === 'error' && <Banner>Couldn’t load currencies: {data.error.message}</Banner>}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      {fx && (
        <div className="el-stack">
          <Card as="section" padding="18px 20px" aria-labelledby="fx-source">
            <h2 id="fx-source" className="el-h2" style={{ marginBottom: 6 }}>Where rates come from</h2>
            <p className="el-hint" style={{ marginTop: 0 }}>A rate you type in for a day always wins. Then your bank’s rate, if connected, then the published rate.</p>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="el-label">Published rates</legend>
              <label className="el-check"><input type="radio" name="fx-src" checked={fx.source === 'ecb'}
                onChange={() => void run(() => source.setFxSettings({ source: 'ecb' }), 'Using ECB reference rates')} />
                <span>ECB reference rates<span className="el-hint" style={{ display: 'block' }}>Every working day at about 4pm; what Revenue accepts</span></span></label>
              <label className="el-check"><input type="radio" name="fx-src" checked={fx.source === 'hmrc'}
                onChange={() => void run(() => source.setFxSettings({ source: 'hmrc' }), 'Using HMRC monthly rates')} />
                <span>HMRC monthly rates<span className="el-hint" style={{ display: 'block' }}>One rate a month; usual for UK VAT</span></span></label>
            </fieldset>
            <FeedPanel fx={fx} onConnect={setConnecting}
              onDisconnect={() => void run(() => source.disconnectFxFeed(), 'Bank rates switched off and the key forgotten')} />
          </Card>

          <YourCurrencies fx={fx} onChange={(codes, done) => void run(() => source.setFxSettings({ currencies: codes }), done)} />

          <TypedRates fx={fx} now={now} onSaved={reload} onError={setError} />
          <RevaluationPanel base={fx.base_currency} now={now} version={version} onPosted={reload} />
        </div>
      )}
      {connecting && <ConnectFeed provider={connecting} onClose={() => setConnecting(null)}
        onConnected={() => { setConnecting(null); toast(`${connecting === 'wise' ? 'Wise' : 'Revolut'} connected: its rates are used from now on`); reload(); }} />}
    </>
  );
}

/** The currencies this business deals in: listed first wherever a currency is picked. */
function YourCurrencies({ fx, onChange }: { fx: FxOverview; onChange: (codes: string[], done: string) => void }) {
  const listed = new Set([fx.base_currency, ...fx.currencies.map((c) => c.code)]);
  const addable = currencyGroups(fx.base_currency, []).slice(1)
    .map((g) => ({ ...g, options: g.options.filter((o) => !listed.has(o.code)) })).filter((g) => g.options.length);
  const [adding, setAdding] = useState('');
  const info = (code: string) => fx.all_currencies.find((c) => c.code === code);
  const add = () => {
    if (!adding) return;
    onChange([...fx.chosen, adding], `${adding} added: it’s listed first on invoices, bills and contacts`);
    setAdding('');
  };
  return (
    <Card as="section" padding="18px 0 8px" aria-labelledby="fx-used">
      <div className="el-card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2 id="fx-used" className="el-h2">Your currencies</h2>
        <span className="el-hint">Today’s rate, {fx.base_currency} per unit</span>
      </div>
      <p className="el-hint" style={{ margin: '0 20px 12px' }}>
        The currencies you trade in. They’re listed first wherever you choose a currency; every other currency is still there under its region.
      </p>
      <div className="el-table-wrap">
        <table className="el-table" aria-label="Your currencies">
          <thead><tr><th scope="col">Currency</th><th scope="col" className="el-num">Rate</th><th scope="col">From</th><th scope="col">Rate date</th><th scope="col"><span className="el-vh">Status</span></th></tr></thead>
          <tbody>
            {fx.currencies.map((c) => (
              <tr key={c.code}>
                <td><strong>{c.code}</strong> <span className="el-hint">{c.name}</span>
                  {info(c.code) && !info(c.code)!.ecb_daily && <div className="el-hint">Monthly rate from HMRC (the ECB doesn’t publish one)</div>}</td>
                <td className="el-num">{c.today ? rate(c.today.rate) : '—'}</td>
                <td>{c.today ? SOURCE[c.today.source] : <Pill tone="warn">No rate</Pill>}</td>
                <td>{c.today ? shortDate(c.today.rate_date) : '—'}</td>
                <td className="el-num">
                  <div className="el-row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                    {c.in_use && <Pill tone="info">In use</Pill>}
                    {c.chosen && (
                      <Button size="sm" variant="ghost" aria-label={`Remove ${c.code} from your currencies`}
                        onClick={() => onChange(fx.chosen.filter((x) => x !== c.code), c.in_use
                          ? `${c.code} is no longer listed first; it stays here while it’s in use`
                          : `${c.code} removed from your currencies`)}>Remove</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {fx.currencies.length === 0 && <tr><td colSpan={5} className="el-muted">Only {fx.base_currency} so far. Add the currencies your customers and suppliers use.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="el-row" style={{ padding: '12px 20px 8px', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field id="fx-add" label="Add a currency">
          <select id="fx-add" className="el-input" value={adding} onChange={(e) => setAdding(e.target.value)} style={{ minWidth: 260 }}>
            <option value="">Choose…</option>
            {addable.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>
        <Button disabled={!adding} onClick={add}>Add</Button>
      </div>
    </Card>
  );
}

function FeedPanel({ fx, onConnect, onDisconnect }: { fx: FxOverview; onConnect: (p: 'wise' | 'revolut') => void; onDisconnect: () => void }) {
  const c = fx.feed_connection;
  return (
    <div style={{ marginTop: 16 }} aria-label="Your bank’s rates" role="group">
      <div className="el-label">Your bank’s rates <span className="el-hint">(optional)</span></div>
      {c ? (
        <div className="el-spread" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <strong>{c.provider === 'wise' ? 'Wise' : 'Revolut Business'}</strong>{' '}
            {c.last_error ? <Pill tone="danger">Not working</Pill> : <Pill tone="ok">Connected</Pill>}
            <div className="el-hint">
              {c.last_error ? c.last_error : c.last_fetched_at ? `Rates fetched ${when(c.last_fetched_at)}` : 'First rates within the hour'}
              {' · '}used for documents dated in the last four days
            </div>
          </div>
          <Button variant="ghost" onClick={onDisconnect}>Disconnect</Button>
        </div>
      ) : (
        <>
          <p className="el-hint" style={{ margin: '4px 0 10px' }}>
            Wise and Revolut Business publish the rate they would give you today. Irish high-street banks don’t offer rates to other software,
            so with AIB, Bank of Ireland or PTSB the published rates are used. Either way, a foreign payment is booked at what the bank actually
            paid, and the difference is posted as an exchange gain or loss.
          </p>
          <div className="el-row" style={{ gap: 8 }}>
            <Button onClick={() => onConnect('wise')}>Connect Wise</Button>
            <Button onClick={() => onConnect('revolut')}>Connect Revolut Business</Button>
          </div>
        </>
      )}
    </div>
  );
}

function ConnectFeed({ provider, onClose, onConnected }: { provider: 'wise' | 'revolut'; onClose: () => void; onConnected: () => void }) {
  const { source } = useSession();
  const [f, setF] = useState({ token: '', client_id: '', refresh_token: '', private_key_pem: '', issuer: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));

  async function save() {
    setBusy(true);
    setError(null);
    const creds: FxFeedCredentials = provider === 'wise'
      ? { provider, token: f.token.trim() }
      : { provider, client_id: f.client_id.trim(), refresh_token: f.refresh_token.trim(), private_key_pem: f.private_key_pem.trim(), issuer: f.issuer.trim() };
    try {
      await source.connectFxFeed(creds);
      onConnected();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={provider === 'wise' ? 'Connect Wise' : 'Connect Revolut Business'} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy} onClick={() => void save()}>Connect</Button></>}>
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
      {provider === 'wise' ? (
        <>
          <p className="el-hint" style={{ marginTop: 0 }}>In Wise Business, go to Settings → Integrations and tools → API tokens and create a <strong>read only</strong> token. Only rates are read with it.</p>
          <Field id="fx-wise-token" label="API token"><input id="fx-wise-token" className="el-input" autoComplete="off" value={f.token} onChange={(e) => set('token', e.target.value)} /></Field>
        </>
      ) : (
        <>
          <p className="el-hint" style={{ marginTop: 0 }}>In Revolut Business, go to Settings → APIs → Business API, upload the certificate for your key and authorise the app. Only rates are read.</p>
          <Field id="fx-rev-client" label="Client ID"><input id="fx-rev-client" className="el-input" autoComplete="off" value={f.client_id} onChange={(e) => set('client_id', e.target.value)} /></Field>
          <Field id="fx-rev-refresh" label="Refresh token"><input id="fx-rev-refresh" className="el-input" autoComplete="off" value={f.refresh_token} onChange={(e) => set('refresh_token', e.target.value)} /></Field>
          <Field id="fx-rev-issuer" label="Redirect domain" hint="The domain you gave Revolut as the OAuth redirect, e.g. ledger.example.ie"><input id="fx-rev-issuer" className="el-input" value={f.issuer} onChange={(e) => set('issuer', e.target.value)} /></Field>
          <Field id="fx-rev-key" label="Private key (PEM)"><textarea id="fx-rev-key" className="el-input" rows={4} style={{ fontFamily: 'monospace', fontSize: 12 }} value={f.private_key_pem} onChange={(e) => set('private_key_pem', e.target.value)} /></Field>
        </>
      )}
      <p className="el-hint" style={{ marginBottom: 0 }}>The key is stored encrypted and can’t be shown again. Disconnecting forgets it.</p>
    </Dialog>
  );
}

function TypedRates({ fx, now, onSaved, onError }: { fx: FxOverview; now: string; onSaved: () => void; onError: (e: string | null) => void }) {
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const others = fx.all_currencies.filter((c) => c.code !== fx.base_currency);
  const [f, setF] = useState({ currency: fx.currencies[0]?.code ?? 'GBP', date: now, rate: '' });
  const ok = /^\d+(\.\d+)?$/.test(f.rate.trim()) && Number(f.rate) > 0;

  async function save(ccy: string, date: string, value: number | null) {
    onError(null);
    try {
      await source.setFxRate(ccy, date, value);
      toast(value === null ? `${ccy} rate for ${shortDate(date)} removed` : `${ccy} rate for ${shortDate(date)} saved`);
      if (value !== null) setF((x) => ({ ...x, rate: '' }));
      onSaved();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card as="section" padding="18px 0 8px" aria-labelledby="fx-typed">
      <div className="el-card-head"><h2 id="fx-typed" className="el-h2">Rates you’ve typed in</h2><span className="el-hint">For a day the feeds don’t cover, or to match your bank</span></div>
      <div className="el-row" style={{ gap: 12, padding: '0 20px 12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field id="fx-t-ccy" label="Currency">
          <select id="fx-t-ccy" className="el-input" value={f.currency} onChange={(e) => setF((x) => ({ ...x, currency: e.target.value }))}>
            {others.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
          </select>
        </Field>
        <Field id="fx-t-date" label="Date"><input id="fx-t-date" type="date" className="el-input" value={f.date} onChange={(e) => setF((x) => ({ ...x, date: e.target.value }))} /></Field>
        <Field id="fx-t-rate" label={`${fx.base_currency} per 1 ${f.currency}`}>
          <input id="fx-t-rate" className="el-input el-num" inputMode="decimal" placeholder="1.1628" value={f.rate} onChange={(e) => setF((x) => ({ ...x, rate: e.target.value }))} />
        </Field>
        <Button disabled={!ok} onClick={() => void save(f.currency, f.date, Number(f.rate))}>Save rate</Button>
      </div>
      <div className="el-table-wrap">
        <table className="el-table" aria-label="Typed rates">
          <thead><tr><th scope="col">Date</th><th scope="col">Currency</th><th scope="col" className="el-num">Rate</th><th scope="col"><span className="el-vh">Remove</span></th></tr></thead>
          <tbody>
            {fx.manual_rates.map((m) => (
              <tr key={`${m.currency}${m.date}`}>
                <td>{shortDate(m.date)}</td><td>{m.currency}</td><td className="el-num">{rate(m.rate)}</td>
                <td className="el-num"><Button size="sm" variant="ghost" aria-label={`Remove the ${m.currency} rate for ${shortDate(m.date)}`}
                  onClick={() => void save(m.currency, m.date, null)}>Remove</Button></td>
              </tr>
            ))}
            {fx.manual_rates.length === 0 && <tr><td colSpan={4} className="el-muted">None. The feeds cover every day so far.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RevaluationPanel({ base, now, version, onPosted }: { base: string; now: string; version: number; onPosted: () => void }) {
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const [asAt, setAsAt] = useState(() => lastMonthEnd(now));
  const [posted, setPosted] = useState(0);
  const data = useAsync(() => source.revaluationPreview(asAt), [source, asAt, version, posted], { keep: true });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [asAt]);

  async function post() {
    setBusy(true);
    setError(null);
    try {
      await source.postRevaluation(asAt);
      toast(`Revaluation at ${shortDate(asAt)} posted; it reverses itself the next day`);
      setPosted((n) => n + 1);
      onPosted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  }

  const p = data.state === 'ready' ? data.data : null;
  const canPost = !!p && !p.already && p.lines.length > 0 && p.missing_rates.length === 0;
  return (
    <Card as="section" padding="18px 0 12px" aria-labelledby="fx-reval">
      <div className="el-card-head">
        <h2 id="fx-reval" className="el-h2">Month-end revaluation</h2>
        <span className="el-hint">Restates what customers, suppliers and foreign accounts are worth at the month-end rate</span>
      </div>
      <div className="el-row" style={{ gap: 12, padding: '0 20px 12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field id="fx-r-date" label="Revalue at"><input id="fx-r-date" type="date" className="el-input" value={asAt} onChange={(e) => setAsAt(e.target.value)} /></Field>
        <Button variant="primary" disabled={!canPost || busy} onClick={() => setConfirm(true)}>Post revaluation</Button>
      </div>
      {error && <div className="el-error-box" role="alert" style={{ margin: '0 20px 12px' }}>{error}</div>}
      {data.state === 'error' && <div style={{ padding: '0 20px' }}><Banner>Couldn’t work it out: {data.error.message}</Banner></div>}
      {p?.already && (
        <div style={{ padding: '0 20px 12px' }}><Banner tone="info">Posted {when(p.already.posted_at!)}. It reverses itself on {shortDate(nextDay(p.as_at))}, so nothing is counted twice.</Banner></div>
      )}
      {p && p.missing_rates.length > 0 && (
        <div style={{ padding: '0 20px 12px' }}><Banner tone="warn">No rate at {shortDate(p.as_at)} for {p.missing_rates.join(', ')}. Type one in above first.</Banner></div>
      )}
      {p && (
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Revaluation">
            <thead><tr>
              <th scope="col">Balance</th><th scope="col" className="el-num">Open amount</th><th scope="col" className="el-num">In the books</th>
              <th scope="col" className="el-num">Rate</th><th scope="col" className="el-num">Revalued</th><th scope="col" className="el-num">Gain or loss</th>
            </tr></thead>
            <tbody>
              {p.lines.map((l) => (
                <tr key={`${l.kind}${l.contact_id ?? l.account_code}${l.currency}`}>
                  <td><strong>{l.name}</strong><div className="el-hint">{KIND[l.kind]}</div></td>
                  <td className="el-num">{signed(l.amount, l.currency)}</td>
                  <td className="el-num">{signed(l.carried, base)}</td>
                  <td className="el-num">{rate(l.rate)}</td>
                  <td className="el-num">{signed(l.revalued, base)}</td>
                  <td className="el-num"><Money value={l.difference.toFixed(2)} currency={base} tone={l.difference < 0 ? 'danger' : 'positive'} /></td>
                </tr>
              ))}
              {p.lines.length === 0 && <tr><td colSpan={6} className="el-muted">Nothing to revalue at {shortDate(p.as_at)}: every foreign balance is already at that day’s rate.</td></tr>}
            </tbody>
            {p.lines.length > 0 && (
              <tfoot><tr><th scope="row" colSpan={5}>{p.total >= 0 ? 'Net gain' : 'Net loss'}</th><td className="el-num"><strong>{signed(p.total, base)}</strong></td></tr></tfoot>
            )}
          </table>
        </div>
      )}
      {confirm && p && (
        <Dialog title={`Post the revaluation at ${shortDate(p.as_at)}?`} onClose={() => setConfirm(false)}
          actions={<><Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button><Button variant="primary" disabled={busy} onClick={() => void post()}>Post</Button></>}>
          <p style={{ marginTop: 0 }}>A {p.total >= 0 ? 'gain' : 'loss'} of {signed(Math.abs(p.total), base)} goes to exchange gains and losses, dated {shortDate(p.as_at)}.</p>
          <p className="el-muted" style={{ marginBottom: 0 }}>It reverses itself the next day, so payments received later are still measured against the original rates.</p>
        </Dialog>
      )}
    </Card>
  );
}

function nextDay(d: string): string {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + 1);
  return x.toISOString().slice(0, 10);
}
